import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Typography,
} from "@mui/material";
import { Pagination } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import axios from "axios";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PostAbsentDialog from "./PostAbsentDialog";
import PostMalPracticeDialog from "./PostMalPracticeDialog";
import PostDebarDialog from "./PostDebarDialog";
import StaffAllocationDialog from "./StaffAllocationDialog";
import ShowCourseDialog from "./ShowCourseDialog";
import MarksEntryDialog from "./MarksEntryDialog";
const ShowExamDatesDialog = ({ open, onClose, examScheduleMasterId, showSnackbar, fetchData }) => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (open && examScheduleMasterId) {
      axios
        .get(
          `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}/${examScheduleMasterId}/dates`
        )
        .then((res) => {
          if (res.data.success) {
            const mapped = res.data.data.map((item, index) => ({ id: index + 1, ...item }));
            setRows(mapped);
            setFilteredRows(mapped);
          }
        })
        .catch((err) => console.error("Error fetching exam dates:", err));
    }
  }, [open, examScheduleMasterId]);

  const columns = useMemo(
    () => [
      { field: "sno", headerName: "S.No", flex: 0.3, minWidth: 60 },
      { field: "program_name", headerName: "Program Name", flex: 1, minWidth: 150 },
      {
        field: "branch_course",
        headerName: "Branch / Course",
        flex: 1,
        minWidth: 400,
        renderCell: (params) => {
          const branch = params.row.branch_name || "";
          const course = params.row.course_name || "";

          return (
            <div
              style={{
                whiteSpace: "pre-line",
                lineHeight: "1.5em",
                paddingTop: 6,
                marginTop: 4,
                paddingBottom: 6,
              }}
            >
              {branch}
              {"\n"}
              {course}
            </div>
          );
        },
      },
      {
        field: "course_type",
        headerName: "CourseType",
        flex: 1,
        minWidth: 100,
        valueGetter: (params) =>
          params.value === "T" ? "Theory" : params.value === "P" ? "Practical" : "",
      },
      {
        field: "exam_datetime",
        headerName: "Exam Date / Time",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const date = params.row.from_exam_date
            ? new Date(params.row.from_exam_date).toLocaleDateString()
            : "";

          const time = params.row.slot_description || "";

          return (
            <div
              style={{
                whiteSpace: "pre-line",
                lineHeight: "1.2em",
                paddingTop: 4,
                marginTop: 4,
                paddingBottom: 4,
              }}
            >
              {date}
              {"\n"}
              {time}
            </div>
          );
        },
      },

      { field: "exam_type_name", headerName: "Exam Type", flex: 1, minWidth: 120 },
      { field: "student_count", headerName: "Count", flex: 0.7, minWidth: 100 },
      { field: "marks_count", headerName: "MarksCount", flex: 0.7, minWidth: 100 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <RowMenu
            row={params.row}
            onGenerateSeatingPlan={handleGenerateSeatingPlan}
            onDownloadBarCodes={onDownloadBarCodes}
            onGenerateAbstractSeatingPlan={onGenerateAbstractSeatingPlan}
            onGenerateRoomCourseStrength={onGenerateRoomCourseStrength}
            onGenerateAttendanceSheet={onGenerateAttendanceSheet}
            onGenerateAwardList={onGenerateAwardList}
            onGenerateMarksList={onGenerateMarksList}
            onGenerateAttendanceList={onGenerateAttendanceList}
            examScheduleMasterId={examScheduleMasterId}
            timetableId={params.row.timetable_id}
            program_id={params.row.program_id}
            branch_id={params.row.branch_id}
            program_name={params.row.program_name}
            exam_slot_id={params.row.exam_slot_id}
            exam_type_id={params.row.exam_type_id}
            slot_description={params.row.slot_description}
            from_exam_date={
              params.row.from_exam_date
                ? new Date(params.row.from_exam_date).toISOString().split("T")[0]
                : ""
            }
            showSnackbar={showSnackbar}
            downloadDFormNew={downloadDFormNew}
          />
        ),
      },
    ],
    [examScheduleMasterId, showSnackbar]
  );

  // Search filter
  useEffect(() => {
    if (!searchText) {
      setFilteredRows(rows);
    } else {
      const lower = searchText.toLowerCase();
      setFilteredRows(
        rows.filter(
          (row) =>
            row.timetable_id?.toString().toLowerCase().includes(lower) ||
            row.program_name?.toLowerCase().includes(lower) ||
            row.branch_name?.toLowerCase().includes(lower) ||
            row.course_name?.toLowerCase().includes(lower) ||
            row.course_type?.toLowerCase().includes(lower) ||
            row.slot_description?.toLowerCase().includes(lower) ||
            row.exam_type_name?.toLowerCase().includes(lower) ||
            new Date(row.from_exam_date).toLocaleDateString().toLowerCase().includes(lower)
        )
      );
    }
  }, [searchText, rows]);

  // Generate Seating Plan + download PDFs
  const handleGenerateSeatingPlan = async (row) => {
    if (!window.confirm("Generate Seating Plan for this exam schedule?")) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/exam-seating-plan/generate-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
        {
          created_by: user.user_email,
        }
      );
      fetchData?.();
      showSnackbar?.("Seating Plan generated successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar?.(err?.response?.data?.message || "Seating Plan generation failed", "error");
    }
    // Download all PDFs
    downloadSeatingPlanNew(row);
    //downloadAttendanceNew(row);
    //downloadAbstractSeatingPlanNew(row);
    //downloadDFormNew(row);
  };

  const downloadFile = async (url, row, suffix) => {
    try {
      const response = await axios.get(url, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      const fileName = `${suffix}.pdf`.replace(/\s+/g, "_");
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(blobUrl, `_blank_${fileName}`);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(`Error downloading ${suffix}:`, err);
    }
  };
  const formatDDMMYYYY = (val) => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d)) return ""; // invalid date

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };
  const onDownloadBarCodes = async (row) => {
    try {
      setLoadingGenerate(true);
      await downloadFile(
        `${API_BASE_URL}/api/exam-seating-plan/barcode-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}/${row.timetable_id}`,
        row,
        `BarCodes_${formatDDMMYYYY(row.from_exam_date)}-${row.program_name}-${row.branch_id}`
      );
    } catch (error) {
      showSnackbar?.("Error generating barcodes", "error");
    } finally {
      setLoadingGenerate(false);
    }
  };

  const downloadSeatingPlanNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-seating-plan-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `Seating_Plan_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );

  const onGenerateAttendanceSheet = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-attendance-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}`,
      row,
      `Attendance_Sheet_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );

  const onGenerateAwardList = (row) => {
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-awardlist-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.course_code}`,
      row,
      `AwardList_${row.course_code}-${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  };

  const onGenerateMarksList = (row) => {
    if (row.from_exam_date == null) alert("Practical Date is not Assigned");
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-markslist-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.course_code}`,
      row,
      `MarksList_${row.course_code}-${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  };

  const onGenerateAttendanceList = (row) => {
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-attendancelist-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.course_code}`,
      row,
      `AttendanceList_${row.course_code}-${formatDDMMYYYY(row.from_exam_date)}-${
        row.slot_description
      }`
    );
  };
  const onGenerateAbstractSeatingPlan = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-abstract-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `Abstract_Seating_Plan_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );

  const onGenerateRoomCourseStrength = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-room-course-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `Room_Course_Strength_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  const downloadAttendanceNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-attendance-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}`,
      row,
      "Attendance_Sheet"
    );
  const downloadAbstractSeatingPlanNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-abstract-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      "Abstract_Seating_Plan"
    );
  const downloadDFormNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/dform-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `DForm_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  const onDownloadDaywiseStrength = async (examScheduleMasterId) => {
    try {
      //setLoadingGenerate(true);

      await downloadFile(
        `${API_BASE_URL}/api/exam-seating-plan/daywise-strength/${clientId}/${collegeId}/${examScheduleMasterId}`,
        examScheduleMasterId,
        `Daywise_Strength_Report`
      );
    } catch (error) {
      showSnackbar?.("Error generating Daywise Strength Report", "error");
    } finally {
      //setLoadingGenerate(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xxl" fullWidth>
        <DialogTitle>
          Exam Dates
          <Typography variant="subtitle2" sx={{ mt: 1, color: "red" }}>
            Note: Generate Barcode sheets for each branch. Generate remaining reports date-wise.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2, mt: 1, width: "50%" }}>
            <TextField
              fullWidth
              margin="normal"
              label="Search"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={() => onDownloadDaywiseStrength(examScheduleMasterId)}
              sx={{
                height: "40px", // match MUI TextField height
                mt: "16px", // align with TextField’s margin
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#1565c0" },
                width: "40%",
              }}
            >
              Daywise Strength
            </Button>
            <Button
              variant="contained"
              onClick={() => onDownloadDaywiseStrength(examScheduleMasterId)}
              sx={{
                height: "40px", // match MUI TextField height
                mt: "16px", // align with TextField’s margin
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#1565c0" },
                width: "40%",
              }}
            >
              Valuation Amount
            </Button>
          </Box>

          <Box
            sx={{
              width: "100%",
              overflowX: "auto", // horizontal scroll
              mt: 2,
            }}
          >
            <Box sx={{ minWidth: 650 }}>
              <Pagination
                count={Math.ceil(filteredRows.length / pageSize)}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}
              />
              <DataGrid
                rows={filteredRows
                  .slice((page - 1) * pageSize, page * pageSize)
                  .map((row, index) => ({
                    ...row,
                    id: row.timetable_id || index,
                    sno: (page - 1) * pageSize + index + 1,
                  }))}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                /* 🔥 CRITICAL FIX */
                pagination
                paginationMode="server" // ✅ prevents double pagination
                rowCount={filteredRows.length}
                page={page - 1} // DataGrid = 0-based
                onPageChange={(newPage) => setPage(newPage + 1)}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                onCellClick={(params, event) => event.stopPropagation()}
                onRowClick={(params, event) => event.stopPropagation()}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={loadingGenerate} onClose={() => setLoadingGenerate(false)}>
        <DialogTitle>Generating Barcodes...</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "300px" }}>
            <LinearProgress variant="indeterminate" />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

// RowMenu component
function RowMenu({
  row,
  onGenerateSeatingPlan,
  examScheduleMasterId,
  timetableId,
  program_id,
  branch_id,
  program_name,
  exam_slot_id,
  exam_type_id,
  slot_description,
  from_exam_date,
  showSnackbar,
  downloadDFormNew,
  onDownloadBarCodes,
  onGenerateAbstractSeatingPlan,
  onGenerateRoomCourseStrength,
  onGenerateAttendanceSheet,
  onGenerateAwardList,
  onGenerateMarksList,
  onGenerateAttendanceList,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [postAbsentsOpen, setPostAbsentsOpen] = useState(false);
  const [postMalPracticesOpen, setPostMalPracticesOpen] = useState(false);
  const [invigilators, setInvigilatorsOpen] = useState(false);
  const [postDebarsOpen, setPostDebarsOpen] = useState(false);
  const [showCourseDialog, setShowCourseDialog] = useState({
    open: false,
    date: null,
    timetable_id: null,
  });
  const [marksEntryOpen, setMarksEntryOpen] = useState(false);
  const [marksCourseRow, setMarksCourseRow] = useState(null);
  const open = Boolean(anchorEl);
  const courseType = (row.course_type || "").toString().trim().toUpperCase();

  const isPractical = courseType.startsWith("P");
  const isTheory = courseType.startsWith("T");
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {isPractical ? (
          /* ================= PRACTICAL COURSE MENU ================= */
          <>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateAwardList(row);
                onGenerateAttendanceList(row);
              }}
              sx={{
                color: "primary.main",
                fontWeight: "bold",
                backgroundColor: "rgba(25,118,210,0.08)",
                "&:hover": { backgroundColor: "rgba(25,118,210,0.15)" },
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Award & Attendance List
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setMarksCourseRow({
                  ...row,
                  student_rollnos: row.student_rollnos,
                  exam_course_id: row.exam_course_id,
                  year_number: row.year_number,
                  semester_number: row.semester_number,
                  external_minimum: row.external_minimum,
                  external_maximum: row.external_maximum,
                  course_code: row.course_code,
                  course_name: row.course_name,
                });
                setMarksEntryOpen(true);
              }}
              sx={{
                color: "success.main",
                fontWeight: "bold",
                backgroundColor: "rgba(46,125,50,0.08)",
                "&:hover": { backgroundColor: "rgba(46,125,50,0.15)" },
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Marks Entry
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateMarksList(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Practical Marks List
            </MenuItem>
          </>
        ) : (
          /* ================= THEORY COURSE MENU ================= */
          <>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onDownloadBarCodes(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              BarCode Sheets
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateSeatingPlan(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Seating Plan
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateAbstractSeatingPlan(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Abstract Seating Plan
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateRoomCourseStrength(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Room Course Strength
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateAttendanceSheet(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Attendance Sheet
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setInvigilatorsOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              View Invigilators
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setPostAbsentsOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Post Absents
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setPostMalPracticesOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Post MalPractice
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setPostDebarsOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Post Debar
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                downloadDFormNew(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              DForm
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setShowCourseDialog({
                  open: true,
                  date: row.from_exam_date,
                  timetable_id: row.timetable_id,
                });
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Upload & Allot Scripts
            </MenuItem>
          </>
        )}
      </Menu>

      <ShowCourseDialog
        open={showCourseDialog.open}
        onClose={() => setShowCourseDialog({ open: false })}
        examScheduleMasterId={examScheduleMasterId}
        examDate={showCourseDialog.date}
        timetableId={showCourseDialog.timetable_id}
        programId={program_id}
        branchId={branch_id}
        showSnackbar={showSnackbar}
      />
      <MarksEntryDialog
        open={marksEntryOpen}
        onClose={() => setMarksEntryOpen(false)}
        courseRow={marksCourseRow}
      />
      <StaffAllocationDialog
        open={invigilators}
        onClose={() => setInvigilatorsOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        exam_slot_id={exam_slot_id} // ✅ renamed correctly
        exam_slot_desc={slot_description}
        from_exam_date={from_exam_date} // ✅ renamed correctly
        showSnackbar={showSnackbar}
      />
      <PostAbsentDialog
        open={postAbsentsOpen}
        onClose={() => setPostAbsentsOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        timetableId={timetableId}
        program_name={program_name}
        exam_slot_id={exam_slot_id}
        exam_type_id={exam_type_id}
        slot_description={slot_description}
        from_exam_date={from_exam_date}
        showSnackbar={showSnackbar}
      />
      <PostMalPracticeDialog
        open={postMalPracticesOpen}
        onClose={() => setPostMalPracticesOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        timetableId={timetableId}
        program_name={program_name}
        exam_slot_id={exam_slot_id}
        exam_type_id={exam_type_id}
        slot_description={slot_description}
        from_exam_date={from_exam_date}
        showSnackbar={showSnackbar}
      />
      <PostDebarDialog
        open={postDebarsOpen}
        onClose={() => setPostDebarsOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        timetableId={timetableId}
        program_name={program_name}
        exam_slot_id={exam_slot_id}
        exam_type_id={exam_type_id}
        slot_description={slot_description}
        from_exam_date={from_exam_date}
        showSnackbar={showSnackbar}
      />
    </>
  );
}

RowMenu.propTypes = {
  row: PropTypes.object.isRequired,
  onGenerateSeatingPlan: PropTypes.func.isRequired,
  onDownloadBarCodes: PropTypes.func.isRequired,
  onGenerateAbstractSeatingPlan: PropTypes.func.isRequired,
  onGenerateRoomCourseStrength: PropTypes.func.isRequired,
  onGenerateAttendanceSheet: PropTypes.func.isRequired,
  onGenerateAwardList: PropTypes.func.isRequired,
  onGenerateMarksList: PropTypes.func.isRequired,
  onGenerateAttendanceList: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timetableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  program_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  program_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  exam_slot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  exam_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  slot_description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  from_exam_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showSnackbar: PropTypes.func,
  downloadDFormNew: PropTypes.func.isRequired,
};

ShowExamDatesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timetableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  program_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  program_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  exam_slot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  exam_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  slot_description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  from_exam_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showSnackbar: PropTypes.func,
  fetchData: PropTypes.func,
};

export default ShowExamDatesDialog;
